import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, BookOpen, BarChart2, ChevronDown, ChevronUp, CheckCircle2, Lock, Play, ShoppingBag } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function pickLang(field, lang, fb = "") {
  if (!field) return fb;
  if (typeof field === "string") return field;
  return field[lang] || field.tr || field.en || fb;
}

function formatSec(sec) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}:${String(s).padStart(2, "0")}` : `${m} dk`;
}

function totalDuration(modules = []) {
  let secs = 0;
  for (const m of modules) for (const l of (m.lessons || [])) secs += l.duration_sec || 0;
  const h = Math.floor(secs / 3600);
  const min = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h} sa ${min} dk` : `${min} dk`;
}

function totalLessons(modules = []) {
  return modules.reduce((acc, m) => acc + (m.lessons || []).length, 0);
}

export default function TrainingDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { add, has } = useCart();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModules, setOpenModules] = useState({});
  const [showEnroll, setShowEnroll] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", email: "", phone: "" });

  useEffect(() => {
    axios.get(`${API}/courses/${slug}`)
      .then(({ data }) => { setCourse(data); setOpenModules({ [data.modules?.[0]?.id]: true }); })
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const enroll = async (e) => {
    e.preventDefault();
    if (!form.name || !form.surname || !form.email) return;
    setEnrolling(true);
    try {
      const { data } = await axios.post(`${API}/courses/${slug}/enroll`, {
        ...form,
        payment_provider: "mock",
      });
      toast.success("Kaydınız tamamlandı! E-posta kutunuzu kontrol edin.");
      setShowEnroll(false);
      navigate(`/trainings/${slug}/watch?eid=${data.enrollment_id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (typeof detail === "string" ? detail : "Bir hata oluştu.");
      toast.error(msg);
    } finally {
      setEnrolling(false);
    }
  };

  const addToCart = () => {
    if (has(course.slug)) { navigate("/cart"); return; }
    if (add(course)) toast.success("Eğitim sepete eklendi.");
  };

  if (loading) return <div className="container-x py-24 text-ink/55 text-center">Yükleniyor…</div>;
  if (!course) return (
    <div className="container-x py-24 text-center">
      <p className="font-serif text-2xl text-ink mb-4">Eğitim bulunamadı.</p>
      <button onClick={() => navigate("/trainings")} className="btn-secondary">Eğitimlere Dön</button>
    </div>
  );

  const priceTL = course.price_kurus > 0
    ? `${(course.price_kurus / 100).toLocaleString("tr-TR")} TL`
    : "Ücretsiz";

  return (
    <div data-testid="training-detail-page">
      {/* Hero */}
      <section className="container-x pt-12 pb-10 md:pt-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Sol — Bilgi */}
          <div className="lg:col-span-7">
            <span className="label-eyebrow">Online Eğitim</span>
            <h1 className="mt-5 font-serif text-4xl md:text-6xl leading-tight tracking-tight text-ink">
              {pickLang(course.title, lang)}
            </h1>
            <p className="mt-6 text-lg text-ink/70 font-light leading-relaxed">
              {pickLang(course.description, lang)}
            </p>

            {/* Meta */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-ink/65">
              <span className="inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-terracotta-400" strokeWidth={1.5} />
                {totalDuration(course.modules)}
              </span>
              <span className="inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-terracotta-400" strokeWidth={1.5} />
                {totalLessons(course.modules)} ders
              </span>
              {course.access_days > 0 && <span className="inline-flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-terracotta-400" strokeWidth={1.5} />
                {course.access_days} günlük erişim
              </span>}
            </div>

            {/* Uzun açıklama */}
            {pickLang(course.long_description, lang) && (
              <div className="mt-8 prose prose-sm max-w-none text-ink/80 font-light leading-relaxed">
                <p>{pickLang(course.long_description, lang)}</p>
              </div>
            )}

            {/* Modüller */}
            {(course.modules || []).length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-ink mb-4">İçerik</h2>
                <div className="space-y-2">
                  {course.modules.map((m, mi) => (
                    <div key={m.id} className="surface-card overflow-hidden">
                      <button
                        onClick={() => setOpenModules((p) => ({ ...p, [m.id]: !p[m.id] }))}
                        className="w-full flex items-center justify-between px-5 py-4 text-left"
                      >
                        <span className="font-medium text-ink">{pickLang(m.title, lang) || `Modül ${mi + 1}`}</span>
                        <span className="flex items-center gap-2 text-sm text-ink/55">
                          <span>{(m.lessons || []).length} ders</span>
                          {openModules[m.id]
                            ? <ChevronUp className="w-4 h-4" strokeWidth={1.5} />
                            : <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
                          }
                        </span>
                      </button>
                      {openModules[m.id] && (
                        <ul className="border-t border-cream-300/60">
                          {(m.lessons || []).map((l, li) => (
                            <li key={l.id} className="flex items-center gap-3 px-5 py-3 border-b border-cream-300/40 last:border-0 text-sm">
                              <Lock className="w-3.5 h-3.5 text-ink/30 shrink-0" strokeWidth={1.5} />
                              <span className="text-ink/70 flex-1">{pickLang(l.title, lang) || `Ders ${li + 1}`}</span>
                              {l.duration_sec > 0 && (
                                <span className="text-ink/40 text-xs">{formatSec(l.duration_sec)}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sağ — Satın Al Kartı */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="surface-card p-7 md:p-8">
              {course.thumbnail && (
                <div className="aspect-[16/9] rounded-xl overflow-hidden mb-6">
                  <img src={course.thumbnail} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-serif text-4xl text-ink">{priceTL}</span>
              </div>

              {course.delivery_mode === "offline" && (
                <div className="mb-5 rounded-xl bg-cream-200/70 p-4 text-sm text-ink/70">
                  {course.start_at && <p>{new Date(course.start_at).toLocaleString("tr-TR", { dateStyle: "long", timeStyle: "short" })}</p>}
                  {course.venue && <p className={course.start_at ? "mt-1" : ""}>{course.venue}</p>}
                  {course.is_last_seats && <p className="mt-3 font-medium text-red-600">Son {course.remaining_capacity} koltuk kaldı.</p>}
                  {course.is_sold_out && <p className="mt-3 font-medium text-red-600">Kontenjan doldu.</p>}
                </div>
              )}

              <button
                onClick={course.price_kurus > 0 ? addToCart : () => setShowEnroll(true)}
                disabled={course.is_sold_out}
                className="btn-accent w-full justify-center mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {course.is_sold_out ? "Kontenjan Doldu" : (course.price_kurus > 0 ? (has(course.slug) ? "Sepete Git" : "Sepete Ekle") : "Ücretsiz Kaydol")}
                {course.price_kurus > 0 ? <ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> : <ArrowRight className="w-4 h-4" strokeWidth={1.5} />}
              </button>

              <ul className="space-y-2.5 text-sm text-ink/70">
                {[
                  course.access_days > 0 ? `${course.access_days} günlük tam erişim` : null,
                  "Video içerikler — tarayıcıda, güvenli",
                  "Not alma ve kaynak indirme",
                  "Tamamlama belgesi",
                ].filter(Boolean).map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-sage-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Kayıt Modalı */}
      {showEnroll && (
        <div
          className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowEnroll(false)}
        >
          <div
            className="surface-card w-full max-w-md p-7 animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-serif text-3xl text-ink mb-2">Kayıt Ol</h2>
            <p className="text-sm text-ink/60 mb-6">{pickLang(course.title, lang)}</p>

            <form onSubmit={enroll} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="admin-label">Ad *</label>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    required
                    className="admin-input"
                  />
                </div>
                <div>
                  <label className="admin-label">Soyad *</label>
                  <input
                    value={form.surname}
                    onChange={(e) => setForm((p) => ({ ...p, surname: e.target.value }))}
                    required
                    className="admin-input"
                  />
                </div>
              </div>
              <div>
                <label className="admin-label">E-posta *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                  className="admin-input"
                />
              </div>
              <div>
                <label className="admin-label">Telefon (opsiyonel)</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className="admin-input"
                />
              </div>

              {course.price_kurus > 0 && (
                <div className="bg-cream-200/60 rounded-xl p-4 text-sm text-ink/65">
                  <strong className="text-ink">Not:</strong> Ödeme entegrasyonu yakında aktif olacak. Şu an örnek kayıt oluşturulmaktadır.
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowEnroll(false)} className="btn-secondary flex-1">
                  İptal
                </button>
                <button type="submit" disabled={enrolling} className="btn-accent flex-1 justify-center disabled:opacity-60">
                  {enrolling ? "Kaydediliyor…" : "Kaydı Tamamla"}
                </button>
              </div>
            </form>

            <style>{`
              .admin-input { width:100%;background:#FBFAF6;border:1px solid #E5E0D8;border-radius:0.75rem;padding:0.65rem 0.9rem;font-family:'Outfit',sans-serif;font-size:0.92rem;color:#2C3531;font-weight:300;transition:all 0.2s; }
              .admin-input:focus { outline:none;border-color:#839788;background:#fff;box-shadow:0 0 0 3px rgba(131,151,136,0.12); }
              .admin-label { display:block;font-size:11px;text-transform:uppercase;letter-spacing:0.18em;color:rgba(44,53,49,0.55);margin-bottom:6px;font-weight:500; }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}
